import type {
  TrainingGroundAnswer,
  TrainingGroundDifficulty,
  TrainingGroundQuestion,
  TrainingGroundSession,
} from "./training-ground-state";

/** Answer value persisted per question inside a draft. */
export type TrainingGroundDraftAnswer = TrainingGroundAnswer;

/** The owned session and unsent draft answers persisted across refresh. */
export type TrainingGroundDraft = {
  version: 2;
  sessionId: number;
  index: number;
  answers: Record<number, TrainingGroundDraftAnswer>;
  difficulties: TrainingGroundDifficulty[];
};

/** Minimal storage contract, narrow enough to fake in tests. */
export type TrainingGroundStorageContract = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

const draftVersion = 2;
const maximumTextAnswerLength = 200;

/** Build the durable storage key without mixing drafts between students. */
export function trainingGroundStorageKey(studentId: number): string {
  return `mntrm:training-ground:v1:${studentId}`;
}

function isValidStudentId(studentId: number): boolean {
  return Number.isInteger(studentId) && studentId > 0;
}

function isTrainingGroundDraftAnswer(
  value: unknown,
  allowText: boolean,
): value is TrainingGroundDraftAnswer {
  if (typeof value === "string") {
    return (
      allowText &&
      value.trim().length > 0 &&
      Array.from(value).length <= maximumTextAnswerLength
    );
  }

  if (Array.isArray(value)) {
    return value.every((item) => Number.isSafeInteger(item) && item >= 0);
  }

  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isTrainingGroundDifficulty(
  value: unknown,
): value is TrainingGroundDifficulty {
  return value === "easy" || value === "medium" || value === "hard";
}

function parseDraft(serializedDraft: string): TrainingGroundDraft | undefined {
  let parsed: unknown;

  try {
    parsed = JSON.parse(serializedDraft);
  } catch {
    return undefined;
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return undefined;
  }

  const record = parsed as Record<string, unknown>;

  if (
    (record.version !== 1 && record.version !== draftVersion) ||
    !Number.isSafeInteger(record.sessionId) ||
    (record.sessionId as number) <= 0 ||
    !Number.isInteger(record.index) ||
    (record.index as number) < 0 ||
    !record.answers ||
    typeof record.answers !== "object" ||
    Array.isArray(record.answers) ||
    (record.difficulties !== undefined &&
      (!Array.isArray(record.difficulties) ||
        !record.difficulties.every(isTrainingGroundDifficulty)))
  ) {
    return undefined;
  }

  const answers: Record<number, TrainingGroundDraftAnswer> = {};
  const allowTextAnswers = record.version === draftVersion;

  for (const [questionId, answer] of Object.entries(
    record.answers as Record<string, unknown>,
  )) {
    if (
      !Number.isSafeInteger(Number(questionId)) ||
      Number(questionId) <= 0 ||
      !isTrainingGroundDraftAnswer(answer, allowTextAnswers)
    ) {
      continue;
    }

    answers[Number(questionId)] = answer;
  }

  return {
    version: draftVersion,
    sessionId: record.sessionId as number,
    index: record.index as number,
    answers,
    difficulties: Array.isArray(record.difficulties) ? record.difficulties : [],
  };
}

/** Read and validate the persisted draft, failing closed on any corruption. */
export function readTrainingGroundDraft(
  storage: TrainingGroundStorageContract | null,
  studentId: number,
): TrainingGroundDraft | undefined {
  if (!storage || !isValidStudentId(studentId)) return undefined;

  try {
    const serializedDraft = storage.getItem(
      trainingGroundStorageKey(studentId),
    );

    return serializedDraft ? parseDraft(serializedDraft) : undefined;
  } catch {
    return undefined;
  }
}

/** Persist the owned session id and unsent draft answers for later resume. */
export function writeTrainingGroundDraft(
  storage: TrainingGroundStorageContract | null,
  studentId: number,
  draft: Omit<TrainingGroundDraft, "version">,
): void {
  if (!storage || !isValidStudentId(studentId)) return;

  try {
    storage.setItem(
      trainingGroundStorageKey(studentId),
      JSON.stringify({ version: draftVersion, ...draft }),
    );
  } catch {
    // Storage may be full, disabled, or blocked; the draft simply won't persist.
  }
}

/** Drop the persisted draft, used once a session is abandoned or stale. */
export function clearTrainingGroundDraft(
  storage: TrainingGroundStorageContract | null,
  studentId: number,
): void {
  if (!storage || !isValidStudentId(studentId)) return;

  try {
    storage.removeItem(trainingGroundStorageKey(studentId));
  } catch {
    // Ignore, nothing left to clean up.
  }
}

/** Keep only draft answers whose question still exists in the resumed session. */
export function restoreTrainingGroundAnswers(
  draftAnswers: Record<number, TrainingGroundDraftAnswer>,
  questions: Array<{
    id: number;
    type?: TrainingGroundQuestion["type"];
  }>,
): Record<number, TrainingGroundDraftAnswer> {
  const questionTypes = new Map(
    questions.map((question) => [question.id, question.type]),
  );
  const restoredAnswers: Record<number, TrainingGroundDraftAnswer> = {};

  for (const [questionId, answer] of Object.entries(draftAnswers)) {
    const numericQuestionId = Number(questionId);
    const questionType = questionTypes.get(numericQuestionId);

    if (!questionTypes.has(numericQuestionId)) continue;
    if (questionType === "fillInTheBlank" && typeof answer !== "string")
      continue;
    if (
      questionType &&
      questionType !== "fillInTheBlank" &&
      typeof answer === "string"
    )
      continue;

    restoredAnswers[numericQuestionId] = answer;
  }

  return restoredAnswers;
}

/** Clamp a persisted question index back into the resumed session's bounds. */
export function restoreTrainingGroundIndex(
  draftIndex: number,
  totalQuestions: number,
): number {
  if (
    !Number.isInteger(draftIndex) ||
    draftIndex < 0 ||
    totalQuestions <= 0 ||
    draftIndex >= totalQuestions
  ) {
    return 0;
  }

  return draftIndex;
}

/** Distinguishes an auto-resume on mount from an explicit history review. */
export type TrainingGroundResumeKind = "auto" | "review";

/** The client-side state to apply once a resume request settles. */
export type TrainingGroundResumeOutcome = {
  session: TrainingGroundSession | undefined;
  index: number;
  answers: Record<number, TrainingGroundDraftAnswer>;
  clearDraft: boolean;
};

/**
 * Merge a resumed session with the locally persisted draft, never trusting
 * draft answers for a session it wasn't written for, and only ever clearing
 * storage when the auto-resume itself could not find an owned session.
 *
 * @example
 * const outcome = resolveTrainingGroundResume({
 *   kind: "auto",
 *   draft: readTrainingGroundDraft(window.localStorage, studentId),
 *   session: fetcher.data?.session,
 * });
 */
export function resolveTrainingGroundResume({
  kind,
  draft,
  session,
}: {
  kind: TrainingGroundResumeKind;
  draft: TrainingGroundDraft | undefined;
  session: TrainingGroundSession | undefined;
}): TrainingGroundResumeOutcome {
  if (!session) {
    return {
      session: undefined,
      index: 0,
      answers: {},
      clearDraft: kind === "auto",
    };
  }

  if (kind === "review" || session.completed) {
    return { session, index: 0, answers: {}, clearDraft: false };
  }

  if (draft?.sessionId !== session.id) {
    return { session, index: 0, answers: {}, clearDraft: kind === "auto" };
  }

  return {
    session,
    index: restoreTrainingGroundIndex(
      draft?.index ?? 0,
      session.totalQuestions,
    ),
    answers: restoreTrainingGroundAnswers(
      draft?.answers || {},
      session.questions,
    ),
    clearDraft: false,
  };
}
